import { GridRowsProp } from '@mui/x-data-grid-pro';
import { getIn } from 'formik';

import { Apuracao, DreListProps, RequestDreListProps } from '../types';

import { FilterValuesProps } from 'hooks/useFilter';
import { brlPrice, camelCase } from 'util/format';

export const findVlItem = (data: DreListProps, variavel: string): string => {
    const item = data?.dreMercadologicaApuracoes.find((apuracao) => apuracao.dreMercadologicaConfigApuracao.dsNomeVariavel === variavel);

    return item ? brlPrice(item.vlTotal) : '--';
};

const getStrStatus = (item: DreListProps): string => {
    const statusInfo = item.dreStatus.dreStatusEnum.split('_');
    const status = camelCase(statusInfo.length > 1 ? statusInfo[1] : statusInfo[0]);

    if (statusInfo.length > 1) {
        return `${camelCase(statusInfo[0], 0)} ${status}`;
    }

    return status;
};

const getDsLoja = (filterLojas: FilterValuesProps | undefined): string => {
    let dsLoja = '';

    if (filterLojas) {
        const [loja] = filterLojas.value;

        dsLoja = `${loja.cdLoja} - ${loja.dsNomeFantasia}`;
    }

    return dsLoja;
};

/**
 * Cria um ID único para cada DRE, baseado no ID da loja e na data de início.
 */
export const getDreId = ({ loja, dtInicio }: DreListProps) => (loja ? `${loja.idLoja}-${dtInicio}` : dtInicio);

export const getRows = ({ data, filterLojas }: { data: DreListProps[] | undefined; filterLojas: FilterValuesProps | undefined }): GridRowsProp => {
    if (Array.isArray(data) && data.length) {
        return data.map((item) => ({
            id: getDreId(item),
            idDreMercadologica: item.idDreMercadologica,
            dreStatusEnum: item.dreStatus.dreStatusEnum,
            dsStatus: getStrStatus(item),
            dsLoja: getDsLoja(filterLojas),
            dsMes: camelCase(item.dsMesExtenso),
            vlCmv: '—',
            vlGastos: '—',
            vlLucroLiquido: '—',
            vlReceitaBruta: '—',
            vlReceitaLiquida: '—',
            dtInicio: item.dtInicio,
            isLoading: true,
            loja: item.loja,
            error: '',
        }));
    }

    return [];
};

export const getRowsWithMultipleLojas = (data: DreListProps[] | undefined): GridRowsProp => {
    if (Array.isArray(data) && data.length) {
        return data.map((item) => ({
            id: getDreId(item),
            dsMes: camelCase(item.dsMesExtenso),
            vlReceitaBruta: findVlItem(item, Apuracao.RECEITA_BRUTA),
            vlReceitaLiquida: findVlItem(item, Apuracao.RECEITA_LIQUIDA),
            vlLucroLiquido: findVlItem(item, Apuracao.LUCRO_LIQUIDO),
            vlCmv: findVlItem(item, Apuracao.CMV),
            vlGastos: findVlItem(item, Apuracao.GASTOS),
        }));
    }

    return [];
};

export const getColumnTooltip = (variavel: string, data?: RequestDreListProps): string => {
    let tooltip = '';

    data?.data.forEach((dreProp) => {
        const apuracao = dreProp.dreMercadologicaApuracoes?.find((apuracao) => apuracao.dreMercadologicaConfigApuracao.dsNomeVariavel === variavel);

        tooltip = apuracao?.dreMercadologicaConfigApuracao?.dsFormula || '';
    });

    return tooltip;
};

export const findValueFilter = (filtros: FilterValuesProps[], column: string): string => filtros.find((filtro) => filtro.property === column)?.value;

export const getDsLojaReport = (filterLoja?: FilterValuesProps): string =>
    filterLoja?.value?.map((item: any) => `${item.cdLoja} - ${item.dsNomeFantasia}`).join(', ');

/**
 * Cria um objeto com as linhas (chaves do objeto) e as configurações correspondentes a cada linha (valores das chaves).
 */
export const getLines = <T>(configuracoes: T[], path: string) => {
    const lines: Record<number, T[]> = {};

    configuracoes.forEach((item, index) => {
        const lineId = Math.floor(getIn(item, `${path}.nrSequencia`) / 10);

        if (!lines[lineId]) {
            lines[lineId] = [];
        }

        lines[lineId].push({ ...item, index });
    });

    return lines;
};
